import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  createTheme,
  ThemeProvider,
  Alert,
  Snackbar,
  Link,
  Chip,
  LinearProgress,
  Typography,
} from '@mui/material';
import io from 'socket.io-client';
import AutorenewIcon from '@mui/icons-material/Autorenew';

import Layout from '../components/Layout';
import { useCompany } from '../contexts/CompanyContext';
import faqService from '../services/faqService';
import FaqEditDialog from '../components/FaqEditDialog';
import FaqDeleteDialog from '../components/FaqDeleteDialog';
import FaqHeader from '../components/FaqHeader';
import FaqContent from '../components/FaqContent';
import FaqUploadDialog from '../components/FaqUploadDialog';
import FaqBulkRegenAudioDialog from '../components/FaqBulkRegenAudioDialog';
import { hasFeature } from '../utils/features';


// 分類顏色對應
const CATEGORY_COLORS = {
  '車輛故障': 'error',
  '操作問題': 'primary',
  '系統設定': 'info',
  '帳號管理': 'success',
  '付款問題': 'warning',
  '其他問題': 'default'
};

// 根據分類取得顏色
const getCategoryColor = (category) => {
  return CATEGORY_COLORS[category] || 'default';
};

const FaqPage = () => {
  // 代理商模式：使用公司切換器選取的 companyId
  const { getEffectiveCompanyId } = useCompany();

  // State management
  const [faqs, setFaqs] = useState([]);
  const [allFaqs, setAllFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingFaq, setSavingFaq] = useState(false);
  const [error, setError] = useState(null);
  const effectiveCompanyId = getEffectiveCompanyId();
  const [companyId, setCompanyId] = useState(() => effectiveCompanyId);
  const [searchQuery, setSearchQuery] = useState('');

  // 代理商切換公司時同步 companyId
  useEffect(() => {
    if (effectiveCompanyId && effectiveCompanyId !== companyId) {
      setCompanyId(effectiveCompanyId);
    }
  }, [effectiveCompanyId]);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginatedFaqs, setPaginatedFaqs] = useState([]);
  
  // Sorting state
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [currentFaq, setCurrentFaq] = useState(null);
  
  // Notification state
  const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });

  // FAQ 標籤回答 Map: { [faqId]: tagAnswers[] }
  const [faqTagAnswersMap, setFaqTagAnswersMap] = useState({});

  // 🔥 追蹤正在背景處理中的 FAQ ID（嵌入向量尚未就緒）
  const [processingFaqIds, setProcessingFaqIds] = useState(new Set());

  // 🔥 記錄當前背景工作 ID
  const [activeJobId, setActiveJobId] = useState(null);

  // 🔥 記錄背景工作的總 FAQ 數量（用於顯示 X/Y 進度）
  const [totalProcessingCount, setTotalProcessingCount] = useState(0);

  // 🔥 Socket 參考（保持穩定連線，避免重複建立/斷開）
  const socketRef = useRef(null);

  // Plan 068 Day 4 — 批次重新產生語音對話方塊狀態
  const [bulkRegenAudioOpen, setBulkRegenAudioOpen] = useState(false);
  const voiceEnabled = hasFeature('voiceEnabled');
  
  // MUI 主題
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: 16,
          },
        },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            margin: '0 4px',
            borderRadius: '4px',
           '&.Mui-selected': {
              backgroundColor: '#e3f2fd',
              color: '#000',
              borderColor: '#90caf9'
            },
          },
        },
      },
    },
  });
  
  // 解析關鍵字
  const parseKeywords = (rawKeywords) => {
    if (!rawKeywords) return [];
    
    try {
      if (Array.isArray(rawKeywords)) {
        return rawKeywords;
      }
      
      if (typeof rawKeywords === 'string') {
        try {
          const parsed = JSON.parse(rawKeywords);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }
      
      if (typeof rawKeywords === 'object') {
        if (rawKeywords.generatedKeywords && Array.isArray(rawKeywords.generatedKeywords)) {
          return rawKeywords.generatedKeywords;
        }
        
        const values = Object.values(rawKeywords);
        if (values.length > 0) {
          return values;
        }
      }
    } catch (e) {
    }

    return [];
  };
  
  // 將文字中的 URL 變成可點擊的連結
  const renderTextWithClickableUrls = (text) => {
    if (!text) return '';
    
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return (
      <Box component="span" sx={{ lineHeight: 1.6 }}>
        {parts.map((part, index) => {
          if (urlRegex.test(part)) {
            return (
              <Link
                key={index}
                href={part}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'underline',
                  wordBreak: 'break-all',
                  display: 'inline',
                  '&:hover': {
                    textDecoration: 'none',
                    backgroundColor: 'primary.light',
                    color: 'white',
                    borderRadius: 1,
                    px: 0.5
                  }
                }}
              >
                {part}
              </Link>
            );
          }
          return <span key={index} style={{ whiteSpace: 'pre-wrap' }}>{part}</span>;
        })}
      </Box>
    );
  };
  
  // 將關鍵字渲染成 Chip
  const renderKeywords = (keywords) => {
    const parsedKeywords = Array.isArray(keywords) 
      ? keywords 
      : parseKeywords(keywords);
    
    if (!parsedKeywords || !parsedKeywords.length) return null;
    
    const visibleKeywords = parsedKeywords.slice(0, 3);
    const remainingCount = parsedKeywords.length - 3;
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {visibleKeywords.map((keyword, index) => (
          <Chip
            key={index}
            label={keyword}
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.75rem',
              height: 24
            }}
          />
        ))}
        {remainingCount > 0 && (
          <Chip
            label={`+${remainingCount}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{
              fontSize: '0.75rem',
              height: 24
            }}
          />
        )}
      </Box>
    );
  };
  
  // 初始載入 FAQ
  useEffect(() => {
    fetchFaqs();
    document.body.classList.add('faq-page');
    
    return () => {
      document.body.classList.remove('faq-page');
    };
  }, [companyId]);
  
  // 排序變動時重新排序
  useEffect(() => {
    if (Array.isArray(allFaqs) && allFaqs.length > 0) {
      sortFaqs();
    }
  }, [sortOrder, allFaqs]);

  // 分頁資料更新
  useEffect(() => {
    if (Array.isArray(faqs)) {
      const startIndex = (page - 1) * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      setPaginatedFaqs(faqs.slice(startIndex, endIndex));
    } else {
      setPaginatedFaqs([]);
    }
  }, [faqs, page, rowsPerPage]);

  // ============================================================
  // 🔥 穩定的 Socket.IO 連線 — 只建立一次，連線後加入公司房間
  // ============================================================
  useEffect(() => {
    const socketUrl = process.env.REACT_APP_SOCKET_URL ||
      process.env.REACT_APP_API_URL?.replace('/api', '') ||
      window.location.origin;
    const token = localStorage.getItem('token');

    // 建立 Socket 連線（整個 FaqPage 生命週期只建立一次）
    try {
      const socket = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });

      // 連線成功後加入公司房間（後端 emitToCompany 廣播到 company_${companyId} 房間）
      socket.on('connect', () => {
        // 加入公司房間，確保能收到 faq_upload_progress 事件
        socket.emit('join_company', {
          companyId: companyId,
          userId: localStorage.getItem('userId') || 'anonymous'
        });
      });

      // 確認加入房間成功
      socket.on('joined_company', (data) => {
      });

      // 重連後自動重新加入房間
      socket.on('reconnect', () => {
        socket.emit('join_company', {
          companyId: companyId,
          userId: localStorage.getItem('userId') || 'anonymous'
        });
      });

      socketRef.current = socket;
    } catch (e) {
    }

    // 組件卸載時斷開
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [companyId]); // 依賴 companyId — 公司切換時重新連線

  // ============================================================
  // 🔥 獨立的事件監聽器 — 只在 activeJobId 改變時註冊/移除
  // ============================================================
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    // 沒有活躍工作時不監聽
    if (!activeJobId) return;

    const handleProgress = (data) => {
      // 忽略不是當前工作的事件（如果有多個上傳）
      if (data.jobId && data.jobId !== activeJobId) return;

      // 根據每筆 FAQ 的狀態更新 processingFaqIds
      if (data.faqStatuses) {
        setProcessingFaqIds(prev => {
          const updated = new Set(prev);
          Object.entries(data.faqStatuses).forEach(([faqId, status]) => {
            const id = parseInt(faqId);
            if (status === 'completed' || status === 'failed') {
              updated.delete(id);
            }
          });
          return updated;
        });
      }

      // 全部完成時清除工作 ID 並重新整理列表
      if (data.status === 'completed' || data.status === 'failed') {
        setActiveJobId(null);
        setTotalProcessingCount(0);
        setProcessingFaqIds(new Set());
        // 延遲重新整理，避免 Socket 事件與資料庫寫入競爭
        setTimeout(() => {
          fetchFaqs(true);
        }, 500);
      }
    };

    socket.on('faq_upload_progress', handleProgress);

    return () => {
      socket.off('faq_upload_progress', handleProgress);
    };
  }, [activeJobId]); // 只依賴 activeJobId — 不會因為 processingFaqIds.size 變動而重新註冊

  // ============================================================
  // Plan 068 Day 4 — 監聽 FAQ 語音狀態變化，即時更新表格列。
  // 後端 emit 'faq_audio_status' { faqId, audioStatus, audioFilePath?, audioDuration?, error? }
  // 我們把對應的 row 直接 patch（避免 refetch），確保使用者點按重新產生後立刻看到狀態切換。
  // ============================================================
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return undefined;

    const handleAudioStatus = (payload) => {
      if (!payload || !payload.faqId) return;
      const merge = (r) => ({
        ...r,
        audioStatus: payload.audioStatus ?? r.audioStatus,
        audio_status: payload.audioStatus ?? r.audio_status,
        audioFilePath: payload.audioFilePath ?? r.audioFilePath,
        audio_file_path: payload.audioFilePath ?? r.audio_file_path,
        audioDuration: payload.audioDuration ?? r.audioDuration,
        audio_duration: payload.audioDuration ?? r.audio_duration,
        // Plan-068 preview-URL fields — patch when regen success event
        // arrives so reopening the dialog finds a valid play URL with
        // a fresh ?v= cache-bust.
        audioPublicUrl: payload.audioPublicUrl ?? r.audioPublicUrl,
        audioUpdatedAt: payload.audioUpdatedAt ?? r.audioUpdatedAt,
      });
      const patcher = (rows) => rows.map((r) => (r.id === payload.faqId ? merge(r) : r));
      setFaqs(patcher);
      setAllFaqs(patcher);
      // Single-source-of-truth pattern (mirror constraint dialog fix): also
      // patch currentFaq so the open FaqEditDialog re-renders with fresh
      // audioInfo without subscribing to its own socket. This is what fixes
      // the "排隊中 stuck" race when the dialog is open during a regen.
      setCurrentFaq((cur) => (cur && cur.id === payload.faqId ? merge(cur) : cur));
    };

    socket.on('faq_audio_status', handleAudioStatus);
    return () => socket.off('faq_audio_status', handleAudioStatus);
  }, [companyId]);

  // ============================================================
  // 🔥 輪詢備援 — 當 Socket 事件未能在合理時間內完成時，主動查詢狀態
  // ============================================================
  useEffect(() => {
    if (!activeJobId) return;

    // 每 10 秒輪詢一次，作為 Socket 的備援
    const pollInterval = setInterval(async () => {
      try {
        const response = await faqService.getUploadJobStatus(activeJobId);
        // API 回傳格式: { success: true, data: { jobId, status, totalFaqs, faqStatuses, ... } }
        const jobData = response?.data || response;

        if (jobData && (jobData.status === 'completed' || jobData.status === 'failed')) {
          setActiveJobId(null);
          setTotalProcessingCount(0);
          setProcessingFaqIds(new Set());
          fetchFaqs(true);
        } else if (jobData && jobData.faqStatuses) {
          // 更新個別 FAQ 狀態
          setProcessingFaqIds(prev => {
            const updated = new Set(prev);
            Object.entries(jobData.faqStatuses).forEach(([faqId, faqStatus]) => {
              const id = parseInt(faqId);
              if (faqStatus === 'completed' || faqStatus === 'failed') {
                updated.delete(id);
              }
            });
            return updated;
          });
        }
      } catch (err) {
      }
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [activeJobId]);

  // 取得 FAQ 列表
  const fetchFaqs = async (preserveCurrentPage = false) => {
    try {
      setLoading(true);
      
      const response = await faqService.getAllFaqs(false, companyId);
      
      // 處理 FAQ 資料
      const faqData = (response.data || []).map((faq, index) => {
        const id = typeof faq.id === 'string' ? parseInt(faq.id, 10) : faq.id;
        
        let keywords = [];
        
        if (faq.raw_keywords) {
          keywords = parseKeywords(faq.raw_keywords);
        } else if (faq.raw_keyword) {
          keywords = parseKeywords(faq.raw_keyword);
        } else if (faq.generatedKeywords) {
          keywords = Array.isArray(faq.generatedKeywords) 
            ? faq.generatedKeywords 
            : parseKeywords(faq.generatedKeywords);
        }
        
        const raw_keywords = faq.raw_keywords || faq.raw_keyword || 
          (Array.isArray(keywords) ? JSON.stringify(keywords) : JSON.stringify([]));
        
        let images = [];
        if (faq.imageIds && Array.isArray(faq.imageIds) && faq.imageIds.length > 0) {
          images = faq.imageIds.map(imageId => ({
            id: imageId,
            url: null,
            isLoading: true
          }));
        } else if (faq.images && Array.isArray(faq.images)) {
          images = faq.images;
        }
        
        return {
          ...faq,
          id: id,
          displayNumber: index + 1,
          keywords: keywords,
          raw_keywords: raw_keywords,
          images: images
        };
      });
      
      // ⚠️ processingFaqIds 由 handleUploadSuccess / Socket / Polling 管理
      // fetchFaqs 不再修改 processingFaqIds，避免 React 批次更新導致的 race condition
      
      // 載入圖片代理 URL
      const faqsWithImages = faqData.filter(faq => faq.images && faq.images.length > 0);
      if (faqsWithImages.length > 0) {
        try {
          const allImageIds = [];
          faqsWithImages.forEach(faq => {
            faq.images.forEach(img => {
              if (img.id && !img.url) {
                allImageIds.push(img.id);
              }
            });
          });
          
          if (allImageIds.length > 0) {
            const batchSize = 50;
            for (let i = 0; i < allImageIds.length; i += batchSize) {
              const batchIds = allImageIds.slice(i, i + batchSize);

              // 🔥 FIX: 相容兩種方法名稱 — getBatchProxyUrls (新) 或 getBatchImageProxyUrls (舊)
              let proxyUrlsResponse = null;
              try {
                if (typeof faqService.getBatchProxyUrls === 'function') {
                  // 新版：直接回傳 { urls: { [id]: url } }
                  proxyUrlsResponse = await faqService.getBatchProxyUrls(batchIds, 'faq', false);
                } else if (typeof faqService.getBatchImageProxyUrls === 'function') {
                  // 舊版：回傳陣列 [{id, url}]，需轉換格式
                  const urlsArray = await faqService.getBatchImageProxyUrls(batchIds, false);
                  if (Array.isArray(urlsArray)) {
                    const urlsMap = {};
                    urlsArray.forEach(item => {
                      if (item.id && item.url) {
                        urlsMap[item.id] = item.url;
                      }
                    });
                    proxyUrlsResponse = { urls: urlsMap };
                  }
                }
              } catch (batchError) {
              }
              
              if (proxyUrlsResponse && proxyUrlsResponse.urls) {
                faqData.forEach(faq => {
                  if (faq.images) {
                    faq.images = faq.images.map(img => {
                      if (img.id && proxyUrlsResponse.urls[img.id]) {
                        return {
                          ...img,
                          url: proxyUrlsResponse.urls[img.id],
                          isLoading: false
                        };
                      }
                      return img;
                    });
                  }
                });
              }
            }
          }
        } catch (imageError) {
        }
      }
      
      setAllFaqs(faqData);

      // 批次載入標籤回答（單次請求取代 N 次）
      try {
        const faqIds = faqData.map(faq => faq.id);
        const batchRes = await faqService.getBatchTagAnswers(faqIds);
        setFaqTagAnswersMap(batchRes?.data || {});
      } catch (e) {
        // 標籤回答載入失敗不影響主流程
      }

      const currentPageNum = page;
      
      sortFaqs(faqData);
      
      if (preserveCurrentPage) {
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          const filteredFaqs = faqData.filter(faq => {
            const questionMatch = faq.question?.toLowerCase().includes(query);
            const answerMatch = faq.answer?.toLowerCase().includes(query);
            const categoryMatch = faq.category?.toLowerCase().includes(query);
            const keywords = parseKeywords(faq.raw_keywords || faq.raw_keyword);
            const keywordMatch = keywords.some(keyword => 
              keyword.toLowerCase().includes(query)
            );
            
            return questionMatch || answerMatch || categoryMatch || keywordMatch;
          });
          
          const sortedFilteredFaqs = [...filteredFaqs].sort((a, b) => {
            const idA = typeof a.id === 'string' ? parseInt(a.id, 10) : (a.id || 0);
            const idB = typeof b.id === 'string' ? parseInt(b.id, 10) : (b.id || 0);
            
            if (sortOrder === 'asc') {
              return idA - idB;
            } else {
              return idB - idA;
            }
          }).map((faq, index) => ({
            ...faq,
            displayNumber: index + 1
          }));
          
          setFaqs(sortedFilteredFaqs);
        }
        
        const totalItems = faqs.length || faqData.length;
        const totalPages = Math.ceil(totalItems / rowsPerPage);
        
        if (currentPageNum > totalPages && totalPages > 0) {
          setPage(totalPages);
        } else if (currentPageNum < 1) {
          setPage(1);
        } else {
          setPage(currentPageNum);
        }
      } else {
        setPage(1);
      }
      
      setLoading(false);
    } catch (err) {
      setError(err.message || '無法載入常見問題');
      setLoading(false);
    }
  };
  
  // 排序 FAQ
  const sortFaqs = (faqsToSort = allFaqs) => {
    if (!Array.isArray(faqsToSort) || faqsToSort.length === 0) return;
    
    const sortedFaqs = [...faqsToSort].sort((a, b) => {
      const idA = typeof a.id === 'string' ? parseInt(a.id, 10) : (a.id || 0);
      const idB = typeof b.id === 'string' ? parseInt(b.id, 10) : (b.id || 0);
      
      if (sortOrder === 'asc') {
        return idA - idB;
      } else {
        return idB - idA;
      }
    });
    
    const updatedFaqs = sortedFaqs.map((faq, index) => ({
      ...faq,
      displayNumber: index + 1
    }));
    
    setFaqs(updatedFaqs);
  };
  
  // 切換排序
  const handleSortOrderChange = (event, newSortOrder) => {
    if (newSortOrder !== null) {
      setSortOrder(newSortOrder);
      
      if (allFaqs && allFaqs.length > 0) {
        setTimeout(() => {
          sortFaqs(allFaqs);
        }, 0);
      }
    }
  };

  // 搜尋
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setFaqs(allFaqs);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filteredFaqs = allFaqs.filter(faq => {
      const questionMatch = faq.question?.toLowerCase().includes(query);
      const answerMatch = faq.answer?.toLowerCase().includes(query);
      const categoryMatch = faq.category?.toLowerCase().includes(query);
      const keywords = parseKeywords(faq.raw_keywords || faq.raw_keyword);
      const keywordMatch = keywords.some(keyword => 
        keyword.toLowerCase().includes(query)
      );
      
      return questionMatch || answerMatch || categoryMatch || keywordMatch;
    });
    
    const sortedFilteredFaqs = [...filteredFaqs].sort((a, b) => {
      const idA = typeof a.id === 'string' ? parseInt(a.id, 10) : (a.id || 0);
      const idB = typeof b.id === 'string' ? parseInt(b.id, 10) : (b.id || 0);
      
      if (sortOrder === 'asc') {
        return idA - idB;
      } else {
        return idB - idA;
      }
    }).map((faq, index) => ({
      ...faq,
      displayNumber: index + 1
    }));
    
    setFaqs(sortedFilteredFaqs);
    setPage(1);
  };

  const handleClear = () => {
    setSearchQuery('');
    setFaqs(allFaqs);
    setPage(1);
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };
  
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleEdit = (faqId) => {
    const faqToEdit = allFaqs.find(faq => faq.id === faqId);
    if (faqToEdit) {
      setCurrentFaq(faqToEdit);
      setEditDialogOpen(true);
    }
  };

  const handleDelete = (faqId) => {
    const faqToDelete = allFaqs.find(faq => faq.id === faqId);
    if (faqToDelete) {
      setCurrentFaq(faqToDelete);
      setDeleteDialogOpen(true);
    }
  };

  const handleCreate = () => {
    setCurrentFaq({ id: null, question: '', answer: '', category: '', raw_keywords: '[]' });
    setEditDialogOpen(true);
  };

  const handleBatchUpload = () => {
    setUploadDialogOpen(true);
  };
  
  const handleUpload = async (file) => {
    if (!file) {
      throw new Error('請選擇檔案');
    }
    
    try {
      const result = await faqService.uploadFaqExcel(file, false);
      return result;
    } catch (error) {
      throw new Error(error.message || '上傳失敗');
    }
  };
  
  // ============================================================
  // 🔥 FIX: 上傳成功後記錄 jobId 和處理中的 FAQ
  // 
  // 後端 uploadFaqExcelAsync 回傳格式:
  //   { message, count, filename, data: [{id, ...}], stats, jobId, processingStatus, insertTimeMs }
  // faqService.uploadFaqExcel 做了 return response.data → result 就是上面那個物件
  //
  // 🔥 關鍵修正：先設定 processingFaqIds 和 totalProcessingCount，
  //    最後再設定 activeJobId — 因為 activeJobId 的 useEffect 會立即觸發
  //    Socket 監聽，此時 processingFaqIds 必須已經準備好
  // ============================================================
  const handleUploadSuccess = (result) => {
    const faqCount = result.count || 0;
    const jobId = result.jobId || null;
    const faqDataArray = result.data || [];

    setNotification({
      open: true,
      message: jobId
        ? `檔案上傳成功，${faqCount} 筆 FAQ 已建立，背景處理進行中...`
        : `檔案上傳成功，${faqCount} 筆 FAQ 已建立`,
      type: 'success'
    });

    // 記錄背景工作 ID — 這會觸發 Socket 事件監聽器和輪詢的 useEffect
    if (jobId) {
      // 🔥 FIX: 先建立全新的 Set（不要讀取舊的 processingFaqIds）
      const newIds = new Set();
      faqDataArray.forEach(faq => {
        if (faq.id) newIds.add(faq.id);
      });

      // 🔥 FIX: 先設定 processingFaqIds 和 totalProcessingCount
      setProcessingFaqIds(newIds);
      setTotalProcessingCount(faqCount);

      // 🔥 FIX: 最後設定 activeJobId — 觸發 Socket 監聽的 useEffect
      // React 會在下一次 render 後才執行 useEffect，
      // 此時 processingFaqIds 和 totalProcessingCount 已經更新
      setActiveJobId(jobId);
    }

    // 重新整理 FAQ 列表（保留當前分頁）
    const currentPageNum = page;
    fetchFaqs(true);
    setTimeout(() => setPage(currentPageNum), 100);
  };

  // 儲存 FAQ
  const handleSaveFaq = async (updatedFaq) => {
    let savedFaqId = updatedFaq.id || null; // 用於標籤回答同步
    try {
      setSavingFaq(true);

      // 代理商模式：帶上目前選取的公司 ID
      if (companyId) {
        updatedFaq.companyId = companyId;
      }

      if (updatedFaq.id) {
        const result = await faqService.updateFaq(updatedFaq.id, updatedFaq, false);
        
        const hasNewImages = updatedFaq.images && updatedFaq.images.some(img => img.file && !img.isExisting);
        
        if (hasNewImages) {
          setEditDialogOpen(false);
          await fetchFaqs(true);
          
          setNotification({
            open: true,
            message: result.message || '常見問題已更新（含圖片）',
            type: 'success'
          });
        } else {
          if (result && result.data) {
            const updatedFaqData = result.data.faq || result.data;
            
            let keywordsArray = [];
            
            if (updatedFaqData.raw_keywords) {
              keywordsArray = parseKeywords(updatedFaqData.raw_keywords);
            } else if (updatedFaqData.raw_keyword) {
              keywordsArray = parseKeywords(updatedFaqData.raw_keyword);
            } else if (updatedFaqData.generatedKeywords) {
              keywordsArray = Array.isArray(updatedFaqData.generatedKeywords)
                ? updatedFaqData.generatedKeywords
                : parseKeywords(updatedFaqData.generatedKeywords);
            }
            
            const formattedFaq = {
              ...updatedFaqData,
              id: updatedFaqData.id,
              displayNumber: updatedFaq.displayNumber,
              question: updatedFaqData.question,
              answer: updatedFaqData.answer,
              category: updatedFaqData.category,
              keywords: keywordsArray,
              raw_keywords: Array.isArray(keywordsArray) 
                ? JSON.stringify(keywordsArray) 
                : (updatedFaqData.raw_keywords || JSON.stringify([]))
            };
            
            const updatedFaqs = allFaqs.map(faq => 
              faq.id === parseInt(updatedFaq.id) ? formattedFaq : faq
            );
            
            setAllFaqs(updatedFaqs);
            sortFaqs(updatedFaqs);
            setEditDialogOpen(false);
            
            setNotification({
              open: true,
              message: result.message || '常見問題已更新',
              type: 'success'
            });
          } else {
            throw new Error('更新回應資料格式錯誤');
          }
        }
      } else {
        const result = await faqService.createFaq(updatedFaq, false);
        // 嘗試從建立結果取得新 FAQ ID
        if (result?.data) {
          const created = result.data.faq || result.data;
          if (created?.id) savedFaqId = created.id;
        }

        const hasImages = updatedFaq.images && updatedFaq.images.length > 0;

        if (hasImages) {
          setEditDialogOpen(false);
          await fetchFaqs(false);
          
          const totalItems = allFaqs.length + 1;
          const totalPages = Math.ceil(totalItems / rowsPerPage);
          
          if (sortOrder === 'asc') {
            setPage(totalPages);
          } else {
            setPage(1);
          }
          
          setNotification({
            open: true,
            message: result.message || '新增常見問題成功（含圖片）',
            type: 'success'
          });
        } else {
          if (result && result.data) {
            const createdFaqData = result.data.faq || result.data;
            savedFaqId = createdFaqData.id; // 記錄新建 FAQ 的 ID

            let keywordsArray = [];

            if (createdFaqData.raw_keywords) {
              keywordsArray = parseKeywords(createdFaqData.raw_keywords);
            } else if (createdFaqData.raw_keyword) {
              keywordsArray = parseKeywords(createdFaqData.raw_keyword);
            } else if (createdFaqData.generatedKeywords) {
              keywordsArray = Array.isArray(createdFaqData.generatedKeywords)
                ? createdFaqData.generatedKeywords
                : parseKeywords(createdFaqData.generatedKeywords);
            }
            
            const newFaq = {
              ...createdFaqData,
              id: createdFaqData.id,
              displayNumber: allFaqs.length + 1,
              question: createdFaqData.question,
              answer: createdFaqData.answer,
              category: createdFaqData.category,
              keywords: keywordsArray,
              raw_keywords: Array.isArray(keywordsArray) 
                ? JSON.stringify(keywordsArray) 
                : (createdFaqData.raw_keywords || JSON.stringify([]))
            };
            
            const updatedFaqs = [...allFaqs, newFaq];
            setAllFaqs(updatedFaqs);
            sortFaqs(updatedFaqs);

            // _keepOpen flag (set by FaqEditDialog when 儲存並產生語音 is
            // clicked in create mode): instead of closing, swap currentFaq to
            // the newly created row so the dialog transitions to edit mode
            // in-place. The dialog's reset useEffect re-syncs audioInfo so
            // the chip shows 排隊中 immediately, and the parent's socket
            // handler patches currentFaq through 產生中 → 已就緒 inline.
            if (updatedFaq._keepOpen) {
              setCurrentFaq(newFaq);
            } else {
              setEditDialogOpen(false);
            }

            const totalPages = Math.ceil(updatedFaqs.length / rowsPerPage);
            if (page !== totalPages && sortOrder === 'asc') {
              setPage(totalPages);
            } else if (sortOrder === 'desc') {
              setPage(1);
            }

            setNotification({
              open: true,
              message: result.message || '新增常見問題成功',
              type: 'success'
            });
          } else {
            throw new Error('建立回應資料格式錯誤');
          }
        }
      }

      // 同步標籤回答（更新和新建共用）— 含每張卡片的圖片
      const { tagAnswerCards, originalAnswerCards } = updatedFaq;

      if (tagAnswerCards && savedFaqId) {
        try {
          // 原始標籤卡片
          const origTagCards = (originalAnswerCards || []).filter(c => c.type === 'tag');
          const origMap = new Map(origTagCards.map(c => [c.tagId, c]));
          const newMap = new Map(tagAnswerCards.map(c => [c.tagId, c]));

          // 刪除已移除的標籤回答（含圖片，由後端處理 S3 清理）
          const deletes = [...origMap.keys()]
            .filter(id => !newMap.has(id))
            .map(id => faqService.deleteTagAnswer(savedFaqId, id).catch(() => {}));
          await Promise.all(deletes);

          // 新增或更新標籤回答（含圖片關聯）
          for (const [tagId, card] of newMap.entries()) {
            if (!card.answer) continue;

            // 收集已存在的圖片 IDs
            let allImageIds = [...(card.existingImageIds || [])];

            // 上傳新圖片（如果有的話）
            const newImages = card.newImages || [];
            if (newImages.length > 0) {
              try {
                // 先 upsert 標籤回答以取得 tagAnswer ID
                const upsertResult = await faqService.createOrUpdateTagAnswer(
                  savedFaqId, tagId, { answer: card.answer, imageIds: allImageIds }
                );

                // 從回傳結果取得 tagAnswer ID，用於上傳圖片時綁定
                const tagAnswerRecord = upsertResult?.data?.tagAnswer;
                const tagAnswerId = tagAnswerRecord?.id || null;

                // 上傳新圖片
                const uploadResult = await faqService.uploadFaqImages(
                  newImages, savedFaqId, false, tagAnswerId
                );
                const uploadedIds = uploadResult.uploadedImages || [];
                allImageIds = [...allImageIds, ...uploadedIds];

                // 更新圖片關聯（含新上傳的）
                if (uploadedIds.length > 0) {
                  await faqService.createOrUpdateTagAnswer(
                    savedFaqId, tagId, { answer: card.answer, imageIds: allImageIds }
                  );
                }
              } catch (imgErr) {
                // 圖片上傳失敗不影響文字回答
                console.warn('標籤回答圖片上傳失敗:', imgErr.message);
                await faqService.createOrUpdateTagAnswer(
                  savedFaqId, tagId, { answer: card.answer, imageIds: allImageIds }
                ).catch(() => {});
              }
            } else {
              // 無新圖片，直接 upsert 文字和現有圖片
              await faqService.createOrUpdateTagAnswer(
                savedFaqId, tagId, { answer: card.answer, imageIds: allImageIds }
              ).catch(() => {});
            }
          }

          // 重新整理標籤回答 Map
          const refreshed = await faqService.getFaqTagAnswers(savedFaqId).catch(() => ({ data: { tagAnswers: [] } }));
          setFaqTagAnswersMap(prev => ({
            ...prev,
            [savedFaqId]: refreshed?.data?.tagAnswers || []
          }));
        } catch (e) {
          // 標籤回答同步失敗不影響主儲存流程
        }
      }

    } catch (err) {
      setNotification({
        open: true,
        message: err.message || '儲存常見問題失敗',
        type: 'error'
      });
    } finally {
      setSavingFaq(false);
    }
  };

  const handleConfirmDelete = async (faqId) => {
    try {
      setLoading(true);
      
      await faqService.deleteFaq(faqId);
      
      const updatedFaqs = allFaqs.filter(faq => faq.id !== faqId);
      
      setAllFaqs(updatedFaqs);
      sortFaqs(updatedFaqs);
      
      setNotification({
        open: true,
        message: '常見問題已刪除',
        type: 'success'
      });
      
      setDeleteDialogOpen(false);
    } catch (err) {
      setNotification({
        open: true,
        message: err.message || '刪除常見問題失敗',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const totalPages = Math.ceil(faqs.length / rowsPerPage);

  return (
    <Layout>
      <ThemeProvider theme={theme}>
        <Box sx={{ 
          width: '100%',
          height: '100vh', 
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Header */}
          <FaqHeader
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleSearch={handleSearch}
            handleClear={handleClear}
            handleKeyPress={handleKeyPress}
            sortOrder={sortOrder}
            handleSortOrderChange={handleSortOrderChange}
            handleBatchUpload={handleBatchUpload}
            handleCreate={handleCreate}
            voiceEnabled={voiceEnabled}
            handleBulkRegenAudio={() => setBulkRegenAudioOpen(true)}
          />

          {/* 🔥 背景處理進度條 — 在 Header 和 Table 之間顯示 */}
          {processingFaqIds.size > 0 && totalProcessingCount > 0 && (
            <Box
              sx={{
                mx: 3,
                mt: 1,
                mb: 0,
                p: 1.5,
                borderRadius: 1,
                bgcolor: '#fff8e1',
                border: '1px solid #ffe082',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexShrink: 0,
              }}
            >
              <AutorenewIcon
                sx={{
                  fontSize: 20,
                  color: '#f57c00',
                  animation: 'spin 1.5s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#e65100' }}>
                    AI 資料處理中...
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                    {totalProcessingCount - processingFaqIds.size} / {totalProcessingCount}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={((totalProcessingCount - processingFaqIds.size) / totalProcessingCount) * 100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: '#ffe0b2',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      bgcolor: '#f57c00',
                      transition: 'transform 0.5s ease',
                    },
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Content — 傳入 processingFaqIds */}
          <FaqContent
            loading={loading}
            error={error}
            faqs={faqs}
            paginatedFaqs={paginatedFaqs}
            renderTextWithClickableUrls={renderTextWithClickableUrls}
            renderKeywords={renderKeywords}
            getCategoryColor={getCategoryColor}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            page={page}
            rowsPerPage={rowsPerPage}
            totalPages={totalPages}
            handlePageChange={handlePageChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
            sortOrder={sortOrder}
            searchQuery={searchQuery}
            processingFaqIds={processingFaqIds}
            faqTagAnswersMap={faqTagAnswersMap}
            voiceEnabled={voiceEnabled}
          />
        </Box>

        <FaqEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          faq={currentFaq}
          onSave={handleSaveFaq}
          isLoading={savingFaq}
        />
        
        <FaqDeleteDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          faq={currentFaq}
          onConfirm={handleConfirmDelete}
        />
        
        <FaqUploadDialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          onUpload={handleUpload}
          onUploadSuccess={handleUploadSuccess}
          templateUrl="/templates/faq_template.xlsx"
        />

        {voiceEnabled && (
          <FaqBulkRegenAudioDialog
            open={bulkRegenAudioOpen}
            onClose={() => setBulkRegenAudioOpen(false)}
            onSuccess={(msg) => setNotification({ open: true, message: msg, type: 'success' })}
            onError={(msg) => setNotification({ open: true, message: msg, type: 'error' })}
          />
        )}

        <Snackbar
          open={notification.open}
          autoHideDuration={4000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.type} 
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>
      {/* FaqChatWidget（測試對話）不移植 —— 那是文字通路的測試工具，
          booking 的客戶是語音，要測是直接打電話（T-038 決策 D3）。 */}
    </Layout>
  );
};

export default FaqPage;